import { Button } from '@/components/ui/button'
import { FilterField } from '@/components/ui/filter-bar'
import { DatePicker } from '@/components/DatePicker'
import { MonthPicker } from '@/components/MonthPicker'
import type { TransactionFilters } from '@/api/transactions'
import {
  currentMonth,
  DATE_PRESETS,
  CUSTOM_MODES,
  type DatePreset,
  type CustomMode,
} from '@/lib/transactionDateRangeFilter'

/** Renders the Date range preset row plus — when Custom is open — the sub-mode row and its
 *  date field(s). Pair with `useDateRangeFilter` (lib/transactionDateRangeFilter) for the
 *  state; callers still supply their own `updateFilter` for wiring the Month/Date/From/To
 *  pickers, so this stays agnostic of how the rest of a page's filter state is structured.
 *  Render inside a `FilterRow`. */
export function DateRangeFilterFields({
  filters,
  activePreset,
  customOpen,
  customMode,
  onSelectPreset,
  onSelectCustomMode,
  updateFilter,
}: {
  filters: Pick<TransactionFilters, 'month' | 'date' | 'dateFrom' | 'dateTo'>
  activePreset: DatePreset
  customOpen: boolean
  customMode: CustomMode
  onSelectPreset: (preset: DatePreset) => void
  onSelectCustomMode: (mode: CustomMode) => void
  updateFilter: (key: 'month' | 'date' | 'dateFrom' | 'dateTo', value: string) => void
}) {
  return (
    <>
      <FilterField label="Date range">
        <div className="flex flex-wrap gap-1 bg-[var(--fl-surface-subtle)] rounded-xl p-1">
          {DATE_PRESETS.map((p) => (
            <Button
              key={p.key}
              type="button"
              variant="ghost"
              onClick={() => onSelectPreset(p.key)}
              className={[
                'h-auto rounded-lg px-3 py-1.5 text-sm font-medium',
                activePreset === p.key
                  ? 'bg-[var(--fl-surface)] text-[var(--fl-text-1)] shadow-sm'
                  : 'text-[var(--fl-text-2)]',
              ].join(' ')}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </FilterField>
      {customOpen && (
        <>
          <FilterField label="Custom">
            <div className="flex flex-wrap gap-1 bg-[var(--fl-surface-subtle)] rounded-xl p-1">
              {CUSTOM_MODES.map((m) => (
                <Button
                  key={m.key}
                  type="button"
                  variant="ghost"
                  onClick={() => onSelectCustomMode(m.key)}
                  className={[
                    'h-auto rounded-lg px-3 py-1.5 text-sm font-medium',
                    customMode === m.key
                      ? 'bg-[var(--fl-surface)] text-[var(--fl-text-1)] shadow-sm'
                      : 'text-[var(--fl-text-2)]',
                  ].join(' ')}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </FilterField>
          {customMode === 'month' && (
            <FilterField label="Month">
              <MonthPicker
                value={filters.month ?? currentMonth()}
                onChange={(value) => updateFilter('month', value)}
              />
            </FilterField>
          )}
          {customMode === 'on' && (
            <FilterField label="Date">
              <DatePicker
                value={filters.date ?? ''}
                onChange={(value) => updateFilter('date', value)}
              />
            </FilterField>
          )}
          {customMode === 'since' && (
            <FilterField label="From">
              <DatePicker
                value={filters.dateFrom ?? ''}
                onChange={(value) => updateFilter('dateFrom', value)}
              />
            </FilterField>
          )}
          {customMode === 'between' && (
            <>
              <FilterField label="From">
                <DatePicker
                  value={filters.dateFrom ?? ''}
                  onChange={(value) => updateFilter('dateFrom', value)}
                />
              </FilterField>
              <FilterField label="To">
                <DatePicker
                  value={filters.dateTo ?? ''}
                  onChange={(value) => updateFilter('dateTo', value)}
                />
              </FilterField>
            </>
          )}
        </>
      )}
    </>
  )
}
